/**
 * Admin -- frozen prompt versioning.
 *
 * PromptVersion rows are immutable once written. Editing a prompt means
 * creating a new row at a new schemaVersion. This page lets ADMINs:
 *   - browse all versions grouped by skill
 *   - read the prompt + system prompt text of any version
 *   - activate / deactivate (only one version per skill is active at a time)
 *
 * The hash field is what production code consults to confirm the prompt it
 * runs matches the audit-row record. Inspector evidence flows from there.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ScrollText, Check, Power, PowerOff, Hash, Copy, ShieldOff } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

interface PromptVersionSummary {
  id: string;
  skillId: string;
  schemaVersion: string;
  hash: string;
  notes: string | null;
  isActive: boolean;
  pinnedModelId: string | null;
  createdAt: string;
  createdBy: string | null;
}

interface PromptVersionDetail extends PromptVersionSummary {
  promptText: string;
  systemPrompt: string;
}

interface SkillGroup {
  skillId: string;
  versions: PromptVersionSummary[];
  activeCount: number;
}

interface ListResp {
  skills: SkillGroup[];
  total: number;
}

export function AdminPromptVersionsPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'ADMIN';

  const list = useQuery({
    queryKey: ['admin', 'prompt-versions'],
    queryFn: async () => (await api.get<ListResp>('/api/admin/prompt-versions')).data,
    enabled: isAdmin, // skip the call entirely for non-ADMIN -- shows the role-gate card instead
    retry: false, // 403 / 401 should surface immediately, not retry 3x with backoff
  });

  const [openSkill, setOpenSkill] = useState<string | null>(null);

  if (!isAdmin) {
    return <RoleGateCard requiredRole="ADMIN" surface="Prompt versions" />;
  }

  if (list.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-72" />
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
      </div>
    );
  }

  if (list.isError) {
    return <RoleGateCard requiredRole="ADMIN" surface="Prompt versions" detail={extractApiError(list.error).message} />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Prompt versions</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Frozen registry of every prompt version Praxara has run in production. Rows are immutable;
          a new version means a new row at a new schemaVersion. Only one version per skill can be
          active. Activations are audit-logged.
        </p>
      </div>

      {list.data && list.data.skills.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No prompt versions registered yet.
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {list.data?.skills.map((s) => {
          const isOpen = openSkill === s.skillId;
          return (
            <Card key={s.skillId} className={cn(s.activeCount === 0 && 'border-amber-300/60')}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 p-4 text-left"
                onClick={() => setOpenSkill(isOpen ? null : s.skillId)}
              >
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  <ScrollText className="h-4 w-4 text-primary" />
                  <span className="font-mono text-sm font-medium">{s.skillId}</span>
                  <span className="text-xs text-muted-foreground">{s.versions.length} version{s.versions.length === 1 ? '' : 's'}</span>
                </div>
                <Badge variant={s.activeCount === 1 ? 'success' : s.activeCount === 0 ? 'warning' : 'destructive'}>
                  {s.activeCount === 0 ? 'no active version' : s.activeCount === 1 ? 'active' : `${s.activeCount} ACTIVE -- bug`}
                </Badge>
              </button>
              {isOpen && (
                <div className="border-t bg-muted/20">
                  {s.versions.map((v) => <VersionRow key={v.id} v={v} />)}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Reusable role-gate card. Shown when the current user lacks the role required
 * to view a route. Replaces the perpetual-skeleton state that surfaces on 403
 * when a non-ADMIN navigates here directly via URL (the sidebar correctly
 * hides the entry, but power users sometimes paste links).
 */
export function RoleGateCard({
  requiredRole,
  surface,
  detail,
}: {
  requiredRole: 'ADMIN' | 'MANAGER' | 'REVIEWER';
  surface: string;
  detail?: string;
}) {
  return (
    <div className="mx-auto max-w-md py-12">
      <Card>
        <CardHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
            <ShieldOff className="h-5 w-5" />
          </div>
          <CardTitle className="text-base">Insufficient permissions</CardTitle>
          <CardDescription>
            <strong>{surface}</strong> requires the {requiredRole} role. You're signed in with a lower role -- ask
            your tenant admin to upgrade, or sign in as an admin user.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-xs">
          {detail && <p className="rounded-md border bg-muted/40 p-2 font-mono text-[10px]">{detail}</p>}
          <Button asChild variant="outline" className="w-full">
            <a href="/">Back to dashboard</a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function VersionRow({ v }: { v: PromptVersionSummary }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const detail = useQuery({
    queryKey: ['admin', 'prompt-versions', v.id],
    queryFn: async () => (await api.get<PromptVersionDetail>(`/api/admin/prompt-versions/${v.id}`)).data,
    enabled: expanded,
  });

  const activate = useMutation({
    mutationFn: async () => (await api.post(`/api/admin/prompt-versions/${v.id}/activate`)).data,
    onSuccess: () => {
      toast.success('Activated', `${v.skillId} schema=${v.schemaVersion} is now the live version.`);
      void qc.invalidateQueries({ queryKey: ['admin', 'prompt-versions'] });
    },
    onError: (err) => toast.error('Activate failed', extractApiError(err).message),
  });

  const deactivate = useMutation({
    mutationFn: async () => (await api.post(`/api/admin/prompt-versions/${v.id}/deactivate`)).data,
    onSuccess: () => {
      toast.success('Deactivated', 'Skill has no active version until you activate one.');
      void qc.invalidateQueries({ queryKey: ['admin', 'prompt-versions'] });
    },
    onError: (err) => toast.error('Deactivate failed', extractApiError(err).message),
  });

  function copyHash() {
    void navigator.clipboard.writeText(v.hash);
    toast.success('Hash copied', 'On the clipboard.');
  }

  return (
    <div className="border-b last:border-b-0">
      <div className="flex flex-wrap items-center justify-between gap-2 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? <ChevronDown className="inline h-3 w-3" /> : <ChevronRight className="inline h-3 w-3" />}
          </button>
          <span className="font-mono text-xs">schema={v.schemaVersion}</span>
          <button
            type="button"
            onClick={copyHash}
            className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono hover:bg-muted/70"
            title={v.hash}
          >
            <Hash className="h-3 w-3" />
            {v.hash.slice(0, 12)}
            <Copy className="h-3 w-3" />
          </button>
          {v.isActive ? (
            <Badge variant="success" className="gap-1">
              <Check className="h-3 w-3" /> active
            </Badge>
          ) : (
            <Badge variant="secondary">archived</Badge>
          )}
          <span className="text-[10px] text-muted-foreground">{formatDate(v.createdAt)}</span>
          {v.notes && <span className="text-[11px] italic text-muted-foreground">"{v.notes}"</span>}
        </div>
        <div className="flex gap-1">
          {v.isActive ? (
            <Button size="sm" variant="outline" onClick={() => deactivate.mutate()} disabled={deactivate.isPending}>
              <PowerOff className="mr-1 h-3 w-3" /> Deactivate
            </Button>
          ) : (
            <Button size="sm" onClick={() => activate.mutate()} disabled={activate.isPending}>
              <Power className="mr-1 h-3 w-3" /> Activate
            </Button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="space-y-3 border-t bg-card p-4">
          {detail.isLoading && <Skeleton className="h-16" />}
          {detail.data && (
            <>
              <PromptBlock label="System prompt" body={detail.data.systemPrompt} />
              <PromptBlock label="User prompt template" body={detail.data.promptText} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PromptBlock({ label, body }: { label: string; body: string }) {
  return (
    <div>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">{label}</CardTitle>
          <CardDescription className="text-[10px]">{body.length.toLocaleString()} chars</CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-3 font-mono text-[11px] leading-relaxed">{body}</pre>
        </CardContent>
      </Card>
    </div>
  );
}
